'use client'

import React, { forwardRef } from 'react'

import { BaseLink } from '@sidecar-components/atoms/BaseLink'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import {
  chevronStyles,
  childrenStyles,
  leftMediaStyles,
  rowBodyStyles,
  rowCardStyles,
  rowFooterStyles,
  rowStyles,
} from './styles'
import { Sentiment } from '@sidecar-components/types'
import { RowProps } from './Row.types'

/**
 * @param {any} [children] - The content of the row
 * @param {string} [className] - Any additional classes to add to the row
 * @param {string} [bodyClass] - Any additional classes to add to the row
 * @param {string} [childrenClass] - Any additional classes to add to the
 *   children
 * @param {any} [leftMedia] - Any media to show on the left of the row
 * @param {any} [link] - If the row should be a link, pass the link here
 * @param {React.ReactNode} linkComponent - Component to use for the next.js
 *   Link.
 * @param {function} [onClick] - If the row should be a button, pass the onClick
 *   here
 * @param {boolean} [borderOverflow] - If the row should have a border pushed to
 *   the edge
 * @param {string} [target] - Whether a link should open in a new window or not
 * @param {boolean} [totalRow] - If the row should have a double border above it
 * @param {boolean} [externalOnClick] - If the row wants the styling and
 *   components that come with an onClick or Link, but cannot use those props
 *   for one reason or another, a common one being that NextJS has it's
 *   own<Link/> components that need to be used for navigation
 * @param {keyof typeof careIcons | keyof typeof systemIcons} [iconName] - The
 *   icon to show on the right of the row
 * @param {React.ReactNode} [footer] - Content that belongs to the row's card
 *   but not to its click target. A link or button row cannot legally contain
 *   another one, and the button variant also sets `[&>*]:pointer-events-none`,
 *   so anything interactive placed in `children` is both invalid and
 *   click-dead. Passing it here renders it inside the same visual card, as a
 *   sibling of the clickable element, where it can hold its own controls.
 * @param {string} [footerClass] - Any additional classes to add to the footer
 * @param {boolean} [reserveIconSpace] - Keep the chevron's footprint even when
 *   no chevron is drawn. For a row that is deliberately not clickable but sits
 *   in a list beside rows that are, so anything the children right-align stays
 *   on the same vertical line down the list.
 * @param {string} [iconPathClass] - Replaces the chevron's default coloring.
 * @returns {React.ReactNode} - A row component
 */
export const Row = forwardRef(
  (
    {
      children,
      className = '',
      bodyClass = '',
      childrenClass = '',
      footer,
      footerClass = '',
      cardClass = '',
      testId,
      iconName = 'chevronRight',
      hideIcon = false,
      leftMedia,
      link,
      linkComponent,
      internalLink,
      onClick,
      reserveIconSpace = false,
      iconPathClass,
      borderOverflow = true,
      sentiment = Sentiment.DEFAULT,
      target,
      totalRow,
      externalOnClick = false,
      ...rest
    }: RowProps,
    forwardedRef: any,
  ) => {
    const hasFooter = !!footer
    const showsIcon =
      (externalOnClick || !!link || !!onClick || !!internalLink) && !hideIcon

    // Links should automatically get a right chevron showing they are a link
    const rowContent = (
      <div
        className={`${rowBodyStyles({
          borderOverflow,
          hasFooter,
          sentiment,
        })} ${bodyClass}`}
      >
        {/* actual row content
          Let the children control number of columns, whether flex is needed, flex-direction, etc */}
        <div
          className={`${childrenStyles({
            sentiment,
          })} ${childrenClass} w-full`}
        >
          {children}
        </div>

        {/* show a small chevron if the row is clickable */}
        {showsIcon && (
          <Icon
            name={iconName}
            size={IconSize.SM}
            pathClass={iconPathClass ?? chevronStyles({ sentiment })}
            ref={forwardedRef}
          />
        )}

        {/* Holds the chevron's place in the flex row when there is no chevron to
            draw, so a row that loses its click target keeps its children the
            same width and whatever they right-align stays put. Matches
            IconSize.SM. */}
        {!showsIcon && reserveIconSpace && (
          <div aria-hidden="true" className="h-4 w-4 shrink-0" />
        )}
      </div>
    )

    // wrap leftMedia in div with padding to make sure it has space.
    // Only really used when the media is taller than the content
    const wrappedLeftMedia = (
      <div className={leftMediaStyles(borderOverflow)}>{leftMedia}</div>
    )

    // rowStyles is applied to all 4 versions of the Row
    const sharedRowStyles = rowStyles({
      borderOverflow,
      hasFooter,
      internalLink,
      link,
      onClick,
      sentiment,
      totalRow,
    })

    /**
     * Without a footer the row is returned exactly as it always was — no extra
     * element, so no consumer's DOM or styling shifts. With one, the card
     * wrapper takes the row's place as the list's child, and the footer sits
     * after the clickable element rather than inside it.
     */
    const withFooter = (row: React.ReactElement) =>
      !hasFooter ? (
        row
      ) : (
        <div className={`${rowCardStyles()} ${cardClass}`}>
          {row}
          <div
            className={`${rowFooterStyles({ borderOverflow, sentiment })} ${footerClass}`}
          >
            {footer}
          </div>
        </div>
      )

    // if the row is a nextjs link, it should use the linkComponent
    if (linkComponent && internalLink) {
      // Capitalised only so the same call can be written as JSX: this is what
      // `React.createElement(linkComponent, …)` compiled to anyway, but with the
      // content between the tags instead of passed as a `children` prop. `rest`
      // stays last, so a caller can still override the attributes above it, and
      // it cannot carry `children` — that prop is destructured by name.
      const LinkComponent = linkComponent

      return withFooter(
        <LinkComponent
          href={internalLink}
          data-testid={testId}
          target={target || '_self'}
          rel="noopener noreferrer"
          className={`${sharedRowStyles} ${className}`}
          ref={forwardedRef}
          {...rest}
        >
          {!!leftMedia && wrappedLeftMedia}
          {rowContent}
        </LinkComponent>,
      )
    }

    // if the row is a link, it should use an <a>
    if (link) {
      return withFooter(
        <a
          className={`${sharedRowStyles} ${className}`}
          href={link}
          target={target || '_blank'}
          rel="noopener noreferrer"
          data-testid={testId}
          ref={forwardedRef}
          {...rest}
        >
          {!!leftMedia && wrappedLeftMedia}
          {rowContent}
        </a>,
      )
    }

    // if you need a react-router link, use the internalLink prop
    if (internalLink) {
      const linkProps = !internalLink.state
        ? { to: internalLink }
        : {
            to: internalLink.pathname,
            state: internalLink.state,
          }

      return withFooter(
        <BaseLink
          className={`${sharedRowStyles} ${className}`}
          rel="noopener noreferrer"
          data-testid={testId}
          ref={forwardedRef}
          target={target || '_self'}
          {...linkProps}
          {...rest}
        >
          {!!leftMedia && wrappedLeftMedia}
          {rowContent}
        </BaseLink>,
      )
    }

    // If we have an onClick, it should be a <button>
    if (onClick) {
      return withFooter(
        <button
          // the extra text-left is because buttons are centered by default
          // [&>*]:pointer-events-none makes heap ignore tracking of the row's children
          className={`${sharedRowStyles} ${className} text-left [&>*]:pointer-events-none`}
          onClick={onClick}
          data-testid={testId}
          ref={forwardedRef}
          // setting button type so a row is never considered a submit button within a form
          type="button"
          {...rest}
        >
          {!!leftMedia && wrappedLeftMedia}
          {rowContent}
        </button>,
      )
    }

    // If we have neither a link or an onClick, it should be a <div>
    return withFooter(
      <div
        className={`${sharedRowStyles} ${className}`}
        data-testid={testId}
        ref={forwardedRef}
        {...rest}
      >
        {!!leftMedia && wrappedLeftMedia}
        {rowContent}
      </div>,
    )
  },
)
